'use client';
import React, { useEffect, useRef, useState } from 'react';
import useClickOutside from 'hooks/useClickOutside';
import ReactSlider from 'react-slider';

interface RangeSliderProps {
  idPrefix: [string, string];
  label: string;
  setfilters: (updater: (prevFilters: any) => any) => void;
  filters: any;
  onchange?: (value: number[]) => void;
  max: number;
  min: number;
  className?: string;
}
const openSliders = new Set<() => void>();
export const closeAllSliders = (): void => {
  openSliders.forEach((close) => close());
  openSliders.clear();
};

let listenerRefCount = 0;
const handleGlobalClick = (e: MouseEvent): void => {
  const target = e.target as HTMLElement;
  if (!target.closest('.minthr-slider-filter')) {
    closeAllSliders();
  }
};

const MinthrSlider: React.FC<RangeSliderProps> = ({
  filters,
  setfilters,
  idPrefix,
  label,
  onchange,
  max,
  min,
  className,
}) => {
  const [value, setValue] = useState<number[]>([min, max]);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [close, setClose] = useState<boolean>(false);

  const dropRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropRef, setDropdownOpen, dropdownOpen);

  useEffect(() => {
    const minValue = filters.filters[idPrefix[0]] ?? min;
    const maxValue = filters.filters[idPrefix[1]] ?? max;
    setValue([minValue, maxValue]);
  }, [filters.filters[idPrefix[0]], filters.filters[idPrefix[1]], min, max]);

  const closeSliders = (): void => {
    setDropdownOpen(false);
    openSliders.delete(closeSliders);
  };

  // Check if slider has been modified from initial values
  const isSliderModified = value[0] !== min || value[1] !== max;

  const toggleDropdown = () => {
    const next = !dropdownOpen;
    setDropdownOpen(next);
    if (next) {
      openSliders.add(closeSliders);
    } else {
      openSliders.delete(closeSliders);
    }
  };

  const sliderChangeHandler = (newValue: number[]) => {
    setDropdownOpen(true);
    setValue(newValue);
    setClose(true);
    setfilters((prevFilters: any) => ({
      ...prevFilters,
      filters: {
        ...prevFilters.filters,
        [idPrefix[0]]: newValue[0],
        [idPrefix[1]]: newValue[1],
      },
    }));
  };

  const handleResetSlider = () => {
    setValue([min, max]);
    setClose(false);
    setfilters((prevFilters: any) => ({
      ...prevFilters,
      filters: {
        ...prevFilters.filters,
        [idPrefix[0]]: min,
        [idPrefix[1]]: max,
      },
    }));
  };

  useEffect(() => {
    listenerRefCount++;
    if (listenerRefCount === 1) {
      document.addEventListener('click', handleGlobalClick);
    }
    return () => {
      openSliders.delete(closeSliders);
      listenerRefCount--;
      if (listenerRefCount === 0) {
        document.removeEventListener('click', handleGlobalClick);
      }
    };
  }, []);

  return (
    <div
      className="dropdown minthr-dropdown minthr-slider-filter minthr-mr-2 minthr-mobile-mr-0 minthr-mobile-mb-2 minthr-mobile-px-2 minthr-mobile-w-50"
      ref={dropRef}
    >
      <button
        type="button"
        className={`dropdown-toggle minthr-dropdown-btn minthr-btn-s minthr-border-rounded minthr-mobile-w-100 minthr-border-primary minthr-btn-filters ${
          isSliderModified ? 'active filter-active show' : ''
        }`}
        onClick={toggleDropdown}
        aria-expanded={dropdownOpen}
        style={
          isSliderModified ? { borderColor: '#28a745', borderWidth: '2px' } : {}
        }
      >
        {label}

        {isSliderModified && (
          <a className="filter-close" onClick={handleResetSlider}>
            <img
              className={`${isSliderModified ? '' : 'minthr-icon-gray'}`}
              src="/icons/close.svg"
              alt="Close filter"
            />
          </a>
        )}
      </button>

      {dropdownOpen && (
        <ul
          className={`dropdown-menu minthr-dropdown-checklist minthr-slider ${className}`}
          style={{ display: 'inline-block' }}
        >
          <li className="minthr-flex minthr-flex-column minthr-flex-items-center minthr-px-2">
            <div className="tooltip-inner minthr-mt-3">
              {value[0]} : {value[1]}
            </div>
            <ReactSlider
              value={value}
              onChange={onchange || sliderChangeHandler}
              className="horizontal-slider"
              thumbClassName="example-thumb"
              trackClassName="example-track"
              ariaLabel={['Lower thumb', 'Upper thumb']}
              min={min}
              max={max}
              ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
            />
          </li>
        </ul>
      )}
    </div>
  );
};

export default MinthrSlider;
